/* eslint-disable multiline-ternary */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  Stack,
  Text,
  useToast
} from '@chakra-ui/react'
import { FaUserTie } from 'react-icons/fa'
import { useIntl } from 'react-intl'
import { getSecretaryService } from '../../../../../services/Services'
import { useRole } from '../../../../../providers/RoleProvider'
import { type SecretaryType } from '../../../../../model/secretary/Secretary'
import messages from './messages'

export default function SecretarySettings() {
  const { doctorId } = useRole()
  const toast = useToast()
  const { formatMessage } = useIntl()
  const [secretaries, setSecretaries] = useState<SecretaryType[]>([])
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [removing, setRemoving] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!doctorId) return
    setLoading(true)
    try {
      setSecretaries(
        await getSecretaryService().getSecretariesByDoctorId(doctorId)
      )
    } catch {
      setSecretaries([])
    } finally {
      setLoading(false)
    }
  }, [doctorId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const occupiedSeats = secretaries.filter(
    (secretary) => secretary.status !== 'disabled'
  ).length

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    try {
      await getSecretaryService().createSecretary({ email, name })
      setName('')
      setEmail('')
      await refresh()
      toast({
        title: formatMessage(messages.invitationSent),
        status: 'success',
        duration: 5000,
        isClosable: true
      })
    } catch (error: any) {
      const code: string = error?.code ?? ''
      toast({
        title:
          code === 'functions/resource-exhausted'
            ? formatMessage(messages.seatLimitReached)
            : code === 'functions/already-exists'
              ? formatMessage(messages.emailAlreadyInUse)
              : formatMessage(messages.anErrorOccurred),
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemove = async (uid: string) => {
    setRemoving(uid)
    try {
      await getSecretaryService().deleteSecretary(uid)
      await refresh()
      toast({
        title: formatMessage(messages.emailCannotBeReused),
        status: 'success',
        duration: 5000,
        isClosable: true
      })
    } catch {
      toast({
        title: formatMessage(messages.failedToRemove),
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    } finally {
      setRemoving(null)
    }
  }

  if (loading) return null

  return (
    <Box
      mt={8}
      p={6}
      bg="white"
      borderRadius="10px"
      borderWidth="1px"
      borderColor="gray.200"
    >
      <Stack spacing={5}>
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={2}>
            <Icon as={FaUserTie} color="gray.600" />
            <Heading size="md" fontWeight="semibold">
              {formatMessage(messages.sectionHeading)}
            </Heading>
          </Flex>
          <Text color="gray.500">{occupiedSeats}/5 seats</Text>
        </Flex>
        <Divider />

        {secretaries.map((secretary) => (
          <Flex
            key={secretary.uid}
            justify="space-between"
            align="center"
            gap={4}
          >
            <Box>
              <Flex align="center" gap={2}>
                <Text fontWeight="bold">{secretary.name}</Text>
                <Badge>{secretary.status}</Badge>
              </Flex>
              <Text color="gray.500">{secretary.email}</Text>
            </Box>
            {secretary.status !== 'disabled' ? (
              <Button
                colorScheme="red"
                variant="outline"
                size="sm"
                isLoading={removing === secretary.uid}
                onClick={() => {
                  handleRemove(secretary.uid).catch(() => undefined)
                }}
              >
                {formatMessage(messages.removeSecretary)}
              </Button>
            ) : null}
          </Flex>
        ))}

        {occupiedSeats < 5 ? (
          <form onSubmit={handleCreate}>
            <Stack spacing={4} pt={2}>
              <Heading size="sm">
                {formatMessage(messages.inviteSecretary)}
              </Heading>
              <FormControl isRequired>
                <FormLabel>{formatMessage(messages.name)}</FormLabel>
                <Input
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value)
                  }}
                  placeholder={formatMessage(messages.namePlaceholder)}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{formatMessage(messages.email)}</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value)
                  }}
                  placeholder={formatMessage(messages.emailPlaceholder)}
                />
              </FormControl>
              <Flex>
                <Button
                  type="submit"
                  colorScheme="gray"
                  isLoading={submitting}
                  loadingText={formatMessage(messages.creatingText)}
                >
                  {formatMessage(messages.sendInvitation)}
                </Button>
              </Flex>
            </Stack>
          </form>
        ) : null}
      </Stack>
    </Box>
  )
}
